// If you update this schemas you will need to update the schemas in the Mastra project as well.
// factorial-agent/src/mastra/agents/talent/trainings/types.ts
import { z } from 'zod'

import { AttachedFile } from 'modules/trainings/lib/content/AttachedFile'
import { ContentModule } from 'modules/trainings/lib/content/Module'
import { ModuleCollection } from 'modules/trainings/lib/content/ModuleCollection'
import { PageBlock } from 'modules/trainings/lib/content/PageBlock'
import { QuizBlock } from 'modules/trainings/lib/content/QuizBlock'
import { VideoBlock } from 'modules/trainings/lib/content/VideoBlock'

//region ai-sync-source
// To use structured output in OpenAi, we must follow their "Supported schemas" as defined in
// https://platform.openai.com/docs/guides/structured-outputs#supported-schemas

const AttachedFileSchema = z.object({
  url: z.string().describe('URL of the attached file'),
  signedId: z
    .string()
    .describe('Signed ID of the attached file if the file is uploaded but not saved yet')
    .nullable(),
  filename: z.string().describe('The filename').nullable(),
})

const FormsElementTypeEnumSchema = z.enum([
  'date',
  'dropdownsingle',
  'longtext',
  'multiselect',
  'number',
  'rating',
  'score',
  'section',
  'select',
  'text',
  'url',
])

const FormElementSchema = z.object({
  id: z.string().nullable(),
  label: z.string(),
  description: z.string().nullable(),
  type: FormsElementTypeEnumSchema,
  mandatory: z.literal(true),
  hasCorrectAnswer: z.literal(true),
  options: z.array(
    z.object({
      id: z.string().nullable(),
      label: z.string(),
      value: z.string(),
      isCorrect: z.boolean().nullable(),
    })
  ),
})

const QuizFormSchema = z.object({
  id: z.string().nullable(),
  description: z.string().nullable(),
  elements: z.array(FormElementSchema),
})

const BaseBlockSchema = z.object({
  uuid: z.string().describe('Unique UUID for the block'),
  title: z.string().describe('blocks label/title'),
})

const PageBlockSchema = BaseBlockSchema.extend({
  type: z.literal('page').describe('Block type: page'),
  description: z.string().describe('Block description'),
  embeddedImages: z.array(AttachedFileSchema).nullable(),
  thumbnail: AttachedFileSchema.nullable(),
})

const QuizBlockSchema = BaseBlockSchema.extend({
  type: z.literal('quiz').describe('Block type: quiz'),
  form: QuizFormSchema,
})

const VideoBlockSchema = BaseBlockSchema.extend({
  type: z.literal('video').describe('Block type: video'),
  video: AttachedFileSchema.nullable(),
})

const BlockSchema = z.union([PageBlockSchema, QuizBlockSchema, VideoBlockSchema])

const ModuleSchema = z.object({
  uuid: z.string().describe('Unique UUID for the module'),
  title: z.string().describe('Module label/title'),
  blocks: z.array(BlockSchema).describe('List of module blocks.'),
})
//endregion

const ModuleCollectionSchema = z.object({
  modules: z
    .array(ModuleSchema)
    .describe('List of content modules. Each module contains many blocks'),
})

export type ModuleCollectionType = z.infer<typeof ModuleCollectionSchema>

export type ContentAgentStateType = {
  content: ModuleCollectionType
  updateContent: boolean
}

/**
 * Populates a `ModuleCollection` instance with modules and their associated blocks
 * from a given state. This function ensures that each module and block is correctly
 * instantiated and added to the collection.
 *
 * @param {ModuleCollection} collection - The collection object to be populated with modules and blocks.
 * @param {ModuleCollectionType} state - The state object containing module and block information to populate the collection.
 *
 * @throws {Error} If a module or block in the state does not have a UUID.
 *
 * Modules and blocks can include the following types:
 * - **Modules**: Represented as instances of `ContentModule`, containing UUIDs and titles.
 * - **Blocks**:
 *   - `page`: Represented as instances of `PageBlock`, with attributes such as UUID, title, description, and a thumbnail.
 *   - `quiz`: Represented as instances of `QuizBlock`, with attributes such as UUID, title, and a form containing quiz elements.
 *   - `video`: Represented as instances of `VideoBlock`, with attributes such as UUID, title, and a video file.
 *
 * Notes on `QuizBlock`:
 * - Quiz elements include IDs, labels, descriptions, types, positions, and options.
 * - Options have attributes to track correctness.
 * - Configuration values include `allowedElements`, retry policies, and scoring.
 */
export const contentFromAi = (collection: ModuleCollection, state: ModuleCollectionType) => {
  collection.clear()

  state.modules.forEach((module) => {
    if (!module.uuid) {
      throw new Error(`Module "${module.title}" does not have a UUID.`)
    }

    // Add the module
    collection.addItem(
      new ContentModule({
        uuid: module.uuid,
        title: module.title,
      })
    )

    //select the module to add all his blocks
    collection.setSelected(module.uuid)

    // Add the blocks
    module.blocks.forEach((block) => {
      if (!block.uuid) {
        throw new Error(`Block "${block.title}" does not have a UUID.`)
      }
      if (block.type === 'page') {
        collection.addItem(
          new PageBlock({
            uuid: block.uuid,
            title: block.title,
            description: block.description || '',
            embeddedImages: block.embeddedImages?.map((image) => AttachedFile.fromAI(image)) || [],
            thumbnail: AttachedFile.fromAI(block.thumbnail),
          })
        )
      } else if (block.type === 'quiz') {
        collection.addItem(
          new QuizBlock({
            uuid: block.uuid,
            title: block.title,
            form: {
              id: block.form.id || undefined,
              title: block.title,
              description: block.form.description,
              resourceId: String(collection.trainingId),
              resourceType: 'Trainings::Training',
              formType: 'quiz',
              elements: block.form.elements.map((element, position) => ({
                id: element.id || undefined,
                label: element.label,
                description: element.description,
                type: element.type,
                position: position,
                mandatory: true,
                hasCorrectAnswer: true,
                options: element.options.map((option) => ({
                  id: option.id || undefined,
                  label: option.label,
                  value: option.value,
                  isCorrect: option.isCorrect || false,
                })),
              })),
              configuration: {
                allowedElements: ['select', 'multiselect'],
                retryable: true,
                scorable: true,
              },
            },
          })
        )
      } else if (block.type === 'video') {
        collection.addItem(
          new VideoBlock({
            uuid: block.uuid,
            title: block.title,
            video: AttachedFile.fromAI(block.video),
          })
        )
      }
    })
  })
}

/**
 * Transforms a `ModuleCollection` into a serialized `ModuleCollectionType` suitable for AI processing.
 *
 * @param {ModuleCollection | undefined} collection - The source collection containing modules and their associated blocks.
 * The blocks could include `PageBlock`, `QuizBlock`, or `VideoBlock` instances.
 *
 * @returns {ModuleCollectionType} A serialized representation of the module collection containing:
 * - The module's UUID and title.
 * - Blocks within the module, categorized as `page`, `quiz`, or `video` with their respective properties:
 *   - `PageBlock` includes UUID, title, type, description, and thumbnail.
 *   - `QuizBlock` includes UUID, title, type, and a detailed form structure, including elements and options.
 *   - `VideoBlock` includes UUID, title, type, and video details.
 *
 * @throws {Error} If an unsupported block type is encountered during serialization.
 */
export const contentToAi = (collection: ModuleCollection | undefined): ModuleCollectionType => {
  if (collection) {
    return {
      modules: collection.modules.map((module: ContentModule) => {
        return {
          uuid: module.uuid,
          title: module.title,
          blocks: module.blocks.map((block) => {
            if (block instanceof PageBlock) {
              return {
                uuid: block.uuid,
                moduleUuid: module.uuid,
                title: block.title,
                type: 'page',
                description: block.description,
                embeddedImages: block.embeddedImages.map((image) => AttachedFile.toAI(image)),
                thumbnail: AttachedFile.toAI(block.thumbnail),
              }
            } else if (block instanceof QuizBlock) {
              return {
                uuid: block.uuid,
                moduleUuid: module.uuid,
                title: block.title,
                type: 'quiz',
                form: {
                  id: block.form?.id || null,
                  description: block.form?.description || null,
                  elements:
                    block.form?.elements.map((element) => ({
                      id: element.id || null,
                      label: element.label,
                      description: element.description || null,
                      type: element.type,
                      mandatory: true,
                      hasCorrectAnswer: true,
                      options:
                        element.options?.map((option) => ({
                          id: option.id || null,
                          label: option.label,
                          value: option.value,
                          isCorrect: option.isCorrect || null,
                        })) || [],
                    })) || [],
                },
              }
            } else if (block instanceof VideoBlock) {
              return {
                uuid: block.uuid,
                moduleUuid: module.uuid,
                title: block.title,
                type: 'video',
                video: AttachedFile.toAI(block.video),
              }
            } else {
              throw new Error(`Invalid block "${block}" while serializing content to AI state.`)
            }
          }),
        }
      }),
    }
  }
  return { modules: [] }
}
